import { Image } from 'expo-image';
import { router } from 'expo-router';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { Button, IconClose, IconErrorFill, Text, tokens } from '@fhs/ui';
import { StyledBlockRenderer } from '@fhs-legacy/frontend/src/pages/loyalty/loyalty-offers/loyalty-offers.styled.default';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import logger from '@fhs-legacy/frontend/src/utils/logger';
import {
  CART_OFFER_APPLIED,
  REMOVE_CART_OFFER_BUTTON,
} from '@fhs-legacy/frontend/src/utils/test-ids';

import { useLoyaltyIncentivesEvaluation } from './use-loyalty-incentives-evaluation';
import { useOfferValidation } from './use-offer-validation';

export function CartOfferItem() {
  useLoyaltyIncentivesEvaluation();

  const { selectedOffer, offersLoadingState } = useAppSelector(state => ({
    selectedOffer: selectors.loyalty.selectSelectedOffer(state),
    offersLoadingState: selectors.loyalty.selectOffersLoading(state),
  }));

  const { isValid, textOfferRequirementsNotMet, isPendingValidation } = useOfferValidation({
    selectedOffer,
  });

  // Log warning when attempting to redeem an offer that is not available
  if (!isValid && selectedOffer) {
    logger.warn(`Attempting to redeem offer that is not available: offer ${selectedOffer._id}`);
  }

  const isReward = selectedOffer?.incentives?.find(
    // explicit any because of incentiveType unknown
    (incentive: any) => incentive?.incentiveType?.toLowerCase() === 'reward'
  );

  const isPending = offersLoadingState || isPendingValidation;

  if (!selectedOffer) {
    return null;
  }

  if (isPending) {
    return (
      <View
        testID={CART_OFFER_APPLIED}
        accessibilityRole="text"
        accessibilityLabel={`Incentive status pending: ${selectedOffer?.name?.localeRaw || 'current incentive'}`}
        accessibilityLiveRegion="polite"
      >
        <View style={styles.pendingContainer}>
          <View style={styles.row}>
            <View style={styles.contentContainer}>
              <View style={styles.pendingIndicatorContainer}>
                <ActivityIndicator size="small" color={tokens.colors.$black} />
              </View>

              <View style={styles.textContainer}>
                <View style={styles.offerAppliedTextBlock}>
                  <Text.Ui size="lg" weight="semibold">
                    {isReward ? 'Reward' : 'Offer'} Status pending
                  </Text.Ui>
                </View>
                <View style={styles.offerNameAndDescription}>
                  <StyledBlockRenderer content={selectedOffer?.name?.localeRaw} />
                </View>
              </View>
            </View>

            <Button
              testID={REMOVE_CART_OFFER_BUTTON}
              type="outline-subtle"
              onPress={() => {
                router.setParams({ action: 'remove-offer', id: selectedOffer?._id });
              }}
              accessibilityRole="button"
              accessibilityLabel={`Remove incentive: ${selectedOffer?.name?.localeRaw || 'current incentive'}`}
              accessibilityHint="Removes the currently applied incentive from your cart"
            >
              <Button.Icon size={16}>
                <IconClose />
              </Button.Icon>
              <Button.Text>Remove</Button.Text>
            </Button>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View
      testID={CART_OFFER_APPLIED}
      accessibilityRole="text"
      accessibilityLabel={
        isValid
          ? `Incentive applied: ${selectedOffer?.name?.localeRaw || 'current incentive'}`
          : `Incentive not applied: ${selectedOffer?.name?.localeRaw || 'current incentive'}`
      }
      accessibilityLiveRegion="polite"
    >
      <View
        style={{
          backgroundColor: isValid
            ? tokens.colors.$successOpacity10
            : tokens.colors.$errorBackground,
        }}
      >
        <View style={styles.row}>
          <View style={styles.contentContainer}>
            <Image
              source={isValid ? require('./offer-icon-green.webp') : require('./offer-icon-red.webp')}
              style={styles.imageContainer}
              accessibilityRole="image"
              accessibilityLabel={isValid ? 'Incentive applied icon' : 'Incentive not applied icon'}
            />

            <View style={styles.textContainer}>
              <View style={styles.offerAppliedTextBlock}>
                <Text.Ui size="lg" weight="semibold">
                  {isReward ? 'Reward' : 'Offer'}
                  {isValid ? ' ' : ' Not '}Applied
                </Text.Ui>
              </View>
              <View style={styles.offerNameAndDescription}>
                <StyledBlockRenderer content={selectedOffer?.name?.localeRaw} />
              </View>
            </View>
          </View>

          <Button
            testID={REMOVE_CART_OFFER_BUTTON}
            type="outline-subtle"
            onPress={() => {
              router.setParams({ action: 'remove-offer', id: selectedOffer?._id });
            }}
            accessibilityRole="button"
            accessibilityLabel={`Remove incentive: ${selectedOffer?.name?.localeRaw || 'current incentive'}`}
            accessibilityHint="Removes the currently applied incentive from your cart"
          >
            <Button.Icon size={16}>
              <IconClose />
            </Button.Icon>
            <Button.Text>Remove</Button.Text>
          </Button>
        </View>
        {!isValid && (
          <View
            style={styles.inlineAlertContainer}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
          >
            <View style={styles.inlineAlertRow}>
              <IconErrorFill size={20} color={tokens.colors.$error} style={styles.inlineAlertIcon} />
              <Text style={styles.inlineAlertText}>{textOfferRequirementsNotMet}</Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    borderBottomColor: 'transparent',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  contentContainer: {
    flexDirection: 'row',
    flex: 1,
    marginRight: 12,
  },
  pendingContainer: {
    backgroundColor: tokens.colors.$blackOpacity10,
  },
  pendingIndicatorContainer: {
    marginRight: 13,
    width: 40,
    height: 41,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageContainer: { marginRight: 13, width: 40, height: 41 },
  textContainer: {
    flexDirection: 'column',
    gap: 3,
    flex: 1,
  },
  offerAppliedTextBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  offerNameAndDescription: {
    flex: 1,
    flexWrap: 'wrap',
  },
  inlineAlertContainer: {
    backgroundColor: tokens.colors.$errorBackground,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  inlineAlertRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  inlineAlertIcon: {
    marginTop: 2,
  },
  inlineAlertText: {
    fontSize: 12,
    lineHeight: 16.8,
    flex: 1,
    color: tokens.colors.$houseDark,
  },
});
